import React, { useState } from 'react';
import GlobalNav from '../components/GlobalNav';
import Footer from '../components/Footer';
import imgDrumpad from './workbook/drumpad.jpg';
import imgRhythm from './workbook/rhythm.jpg';
import imgAlphariddims from './workbook/alphariddims.jpg';
import imgProverbs from './workbook/proverbs.jpg';
import './Storytime.css';

const MODULES = [
  {
    img: imgDrumpad,
    name: 'Drum Pad',
    desc: 'Play bass, tone, and slap using touch or your keyboard. No drum needed.',
    cta: 'Play it now',
    variant: 'amber',
    url: '/drumpad',
  },
  {
    img: imgRhythm,
    name: 'Rhythm Explorer',
    desc: 'Explore Euclidean rhythms from around the world.',
    cta: 'Explore',
    variant: 'purple',
    url: '/rhythmexplorer',
  },
  {
    img: imgAlphariddims,
    name: 'Alphariddims',
    desc: "Every letter has a beat. Spell your child's name and drum it out together.",
    cta: 'Try it',
    variant: 'sky',
    url: '/alphariddims',
  },
  {
    img: imgProverbs,
    name: 'Drum Talk Proverbs',
    desc: 'Drum 30 African proverbs by syllable. Play, read, and explore by region.',
    cta: 'Explore',
    variant: 'pink',
    url: '/drumtalk',
  },
];

const LOGISTICS = [
  { icon: '🕐', label: 'When', value: 'Wednesday, July 1 · 10:30 AM' },
  { icon: '📍', label: 'Where', value: 'Truro Public Library', sub: '7 Standish Way, North Truro, MA' },
  { icon: '👥', label: 'Who', value: 'Children ages 3–6', sub: 'Parents and caregivers welcome and encouraged to stay' },
  { icon: '🎟', label: 'Cost', value: 'Always free', sub: 'Every adult receives a free take-home workbook' },
];

const SHARE_TITLE = 'Urban Griots Playground Storytime';
const SHARE_TEXT = 'Free drumming & storytime for kids 3–6 — Wednesday, July 1 at Truro Public Library.';

function ShareBar() {
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;
  const getUrl = () => (typeof window !== 'undefined' ? window.location.href : 'https://www.musicishealing.us/storytime');

  async function handleNativeShare() {
    try {
      await navigator.share({ title: SHARE_TITLE, text: SHARE_TEXT, url: getUrl() });
    } catch (e) { /* user cancelled */ }
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(getUrl());
    } catch (e) {
      window.prompt('Copy this link:', getUrl());
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  }

  return (
    <div className="story-share">
      {canNativeShare && (
        <button className="story-share-btn" onClick={handleNativeShare} aria-label="Share">↗</button>
      )}
      <a
        className="story-share-btn"
        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(getUrl())}`}
        target="_blank" rel="noopener noreferrer" aria-label="Share on X"
      >𝕏</a>
      <a
        className="story-share-btn"
        href={`mailto:?subject=${encodeURIComponent(SHARE_TITLE)}&body=${encodeURIComponent(`${SHARE_TEXT}\n\n${getUrl()}`)}`}
        aria-label="Share by email"
      >✉</a>
      <button className="story-share-btn" onClick={handleCopyLink} aria-label="Copy link">🔗</button>
      {copied && <span className="story-share-toast">Link copied!</span>}
    </div>
  );
}

function ModuleCard({ mod }) {
  return (
    <a className={`story-module story-module--${mod.variant}`} href={mod.url} target="_blank" rel="noopener noreferrer">
      <div className="story-module-img"><img src={mod.img} alt={mod.name} /></div>
      <div className="story-module-body">
        <div className="story-module-name">{mod.name}</div>
        <p className="story-module-desc">{mod.desc}</p>
        <span className="story-module-cta">{mod.cta} →</span>
      </div>
    </a>
  );
}

export default function Storytime() {
  return (
    <div className="story-page">
      <GlobalNav activeHref="/programs" />

      {/* Hero */}
      <section className="story-hero">
        <h1 className="story-hero-title">
          <span className="story-amber">Urban Griots</span>{' '}
          <span className="story-pink">Playground</span>{' '}
          <span className="story-white">Storytime</span>
        </h1>
        <p className="story-hero-sub">
          An interactive session featuring African drumming, rhythmic movement, and
          multimodal storytelling designed to enhance early literacy, socio-emotional,
          and STEAM skills.
        </p>
        <div className="story-hero-share">
          <span className="story-hero-share-label">Share</span>
          <ShareBar />
        </div>
      </section>

      {/* Details */}
      <section className="story-details">
        <div className="story-eyebrow">Upcoming</div>
        <h2 className="story-section-title">Wednesday, July 1 · Truro Public Library</h2>

        <div className="story-details-grid">
          <div className="story-logistics">
            {LOGISTICS.map((l) => (
              <div className="story-logistic-row" key={l.label}>
                <div className="story-logistic-icon">{l.icon}</div>
                <div>
                  <div className="story-logistic-label">{l.label}</div>
                  <div className="story-logistic-value">{l.value}</div>
                  {l.sub && <div className="story-logistic-sub">{l.sub}</div>}
                </div>
              </div>
            ))}
          </div>

          <img className="story-details-img" src="/images/events/Truro_Storytime.jpg" alt="Storytime" />

          <div className="story-expect">
            <div className="story-expect-label">What to expect</div>
            <p className="story-expect-text">
              Kofi the griot leads children on an adventure quest — counting leopard
              spots, helping a monkey swing through trees, and crossing a river
              together. Each animal leaves the troupe a rhythm to keep.
            </p>
          </div>
        </div>
      </section>

      {/* Workbook + modules */}
      <section className="story-workbook">
        <div className="story-eyebrow story-eyebrow--light">Take-home materials</div>
        <h2 className="story-section-title story-section-title--light">
          Keep playing after the session.
        </h2>
        <p className="story-workbook-sub">
          Every adult leaves with a free <strong className="story-amber">Drum Talk</strong> workbook.
          Each section has a companion digital activity your child can do at home — no drum needed.
        </p>

        <div className="story-workbook-card">
          <img src="/images/events/drumtalk-workbook-cover.png" alt="Drum Talk Workbook" className="story-workbook-cover" />
          <div className="story-workbook-info">
            <div className="story-workbook-name">Drum Talk Workbook</div>
            <p className="story-workbook-desc">
              Free PDF — print at home or pick one up at the event. Includes
              Alphariddims, Praise Poem, Numeriddims, and Syllariddims.
            </p>
          </div>
          <a className="story-download-btn" href="/downloads/drum-talk-workbook.pdf" download>
            ⬇ Download
          </a>
        </div>

        <div className="story-modules-label">Interactive modules inside the workbook</div>
        <div className="story-modules-grid">
          {MODULES.map((m) => <ModuleCard key={m.name} mod={m} />)}
        </div>
      </section>

      <div className="story-credit-bar">
        <span><span className="story-amber">Urban Griots</span> Playground</span>
        <span>Presented by Music Is Healing</span>
      </div>

      <Footer />
    </div>
  );
}
